import { useEffect, useMemo, useState } from 'react';

import { apiOrchestrator } from 'services/apiService';

type RawTriggerDefinition = {
  id: number;
  uuid: string;
  event_type: string;
  name: string;
  description: string;
  service: string;
  config_schema?: any;
  condition_definitions: Array<{
    uuid: string;
    key: string;
    label: string;
    supported_operators: string[];
  }>;
  action_definitions: Array<{
    uuid: string;
    action_type: string;
    name: string;
    config_schema?: any;
  }>;
};

export type Plugin = {
  triggerDefinitionUuid: string;
  eventType: string;
  conditionFields: Array<{
    key: string;
    label: string;
    operators: Array<'EQUALS' | 'NOT_EQUALS' | 'CONTAINS' | 'NOT_CONTAINS' | 'IN' | 'NOT_IN'>;
    valueKind: 'string' | 'string[]' | 'number' | 'datetime';
  }>;
  actionUuids: Record<'add_user_to_team' | 'create_team', string | undefined>;
};

const OP_MAP: Record<string, Plugin['conditionFields'][number]['operators'][number]> = {
  equals: 'EQUALS',
  not_equals: 'NOT_EQUALS',
  contains: 'CONTAINS',
  not_contains: 'NOT_CONTAINS',
  in: 'IN',
  not_in: 'NOT_IN',
};

function normalizeOperators(list: string[]) {
  return list
    .map(s => s.replace(/^'+|'+$/g, ''))
    .map(s => OP_MAP[s])
    .filter(Boolean) as Plugin['conditionFields'][number]['operators'];
}

export function useAutomationDefinitions() {
  const [data, setData] = useState<RawTriggerDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    apiOrchestrator
      .get<RawTriggerDefinition[]>('/automations/trigger-definitions')
      .then(r => {
        if (live) setData(r.data);
      })
      .catch(e => {
        if (live) setError(e);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const pluginFor = useMemo(() => {
    return (eventType: string): Plugin | undefined => {
      const def = data.find(d => d.event_type === eventType);
      if (!def) return undefined;

      const conditionFields = def.condition_definitions.map(cd => ({
        key: cd.key,
        label: cd.label,
        operators: normalizeOperators(cd.supported_operators),
        valueKind: 'string' as const,
      }));

      const findActionUuidByName = (needle: string) =>
        def.action_definitions.find(a => a.name.toLowerCase().includes(needle))?.uuid;

      return {
        triggerDefinitionUuid: def.uuid,
        eventType: def.event_type,
        conditionFields,
        actionUuids: {
          add_user_to_team: findActionUuidByName('adicionar usuário a grupo'),
          create_team: findActionUuidByName('criar grupo'),
        },
      };
    };
  }, [data]);

  return { loading, error, definitions: data, pluginFor };
}
